/**
 * CARD STATE MACHINE (B8). Pure, so the choreography rules are unit-tested.
 *
 *  first open   hidden → open at the dock (Tarr − 120), all content at once
 *  A → B        the head (strip, meta, name, locality) cross-fades at t = 0,
 *               the body goes quiet and swaps at B's dock (§E.3)
 *  deselect     open → leaving (294 leave) → hidden
 *  drive        the phase hides the card (leaving); it re-enters afterwards
 *  embedded     (mobile sheet) the head shows at once, the body at the dock;
 *               closing is immediate because the sheet owns the motion
 *
 * Every function returns the same object when nothing changes, so React bails out.
 */
export type Dir = -1 | 0 | 1;

export interface Slot {
  id: string;
  key: number;
  /** prev/next travel: −1 previous, 1 next, 0 none (cross-fade only). */
  dir: Dir;
  /** The body arrives with the meter landing in the Facts. */
  land?: boolean;
  /** Swapped in while the card was open (fades in place; a fresh entry does not). */
  swap?: boolean;
}

export interface CardState {
  mode: "hidden" | "open" | "leaving";
  /** Increments on every fresh entry (restarts the enter animation). */
  mount: number;
  head: Slot | null;
  /** The outgoing head during an A → B cross-fade. */
  out: Slot | null;
  body: Slot | null;
  /** The body waits for the dock. */
  pending: boolean;
  n: number;
}

export interface CardInput {
  id: string | null;
  docked: boolean;
  drew: boolean;
  /** The phase keeps the card away (drive, loading, intro, the arrival hold). */
  suppressed: boolean;
  embedded: boolean;
  dir: Dir;
}

export const CARD_INITIAL: CardState = { mode: "hidden", mount: 0, head: null, out: null, body: null, pending: false, n: 0 };

export function cardReduce(s: CardState, i: CardInput): CardState {
  if (!i.id || i.suppressed) {
    if (s.mode !== "open") return s;
    return i.embedded ? { ...s, mode: "hidden", head: null, out: null, body: null, pending: false } : { ...s, mode: "leaving", out: null };
  }
  if (s.mode === "open") {
    let next = s;
    let n = s.n;
    if (s.head?.id !== i.id) {
      n += 1;
      next = { ...next, out: s.head, head: { id: i.id, key: n, dir: i.dir, swap: true } };
    }
    if (i.docked) {
      if (next.body?.id !== i.id) {
        n += 1;
        next = { ...next, body: { id: i.id, key: n, dir: next.head?.dir ?? 0, land: i.drew, swap: true } };
      }
      if (next.pending) next = { ...next, pending: false };
    } else if (next.body?.id !== i.id && !next.pending) {
      next = { ...next, pending: true };
    }
    return next === s ? s : { ...next, n };
  }
  // hidden or leaving: a fresh entry once the numbers are ready (embedded: at once, the body waits)
  if (!i.docked && !i.embedded) return s;
  const n = s.n + 2;
  return {
    mode: "open",
    mount: s.mount + 1,
    head: { id: i.id, key: n - 1, dir: 0 },
    out: null,
    body: i.docked ? { id: i.id, key: n, dir: 0, land: i.drew } : null,
    pending: !i.docked,
    n,
  };
}

/** The outgoing head has faded (168 ms). */
export function dropOut(s: CardState, key: number): CardState {
  return s.out && s.out.key === key ? { ...s, out: null } : s;
}

/** The exit has played (294 ms). */
export function finishLeave(s: CardState): CardState {
  return s.mode === "leaving" ? { ...s, mode: "hidden", head: null, out: null, body: null, pending: false } : s;
}

/** Durations (ms) the component schedules (spec §E.2, §E.3, §E.10). */
export const OUT_MS = 168;
export const IN_MS = 240;
export const LEAVE_MS = 294;
export const REDUCED_MS = 160;
